/**
 * Tree flatten + subsumption — the pure-data cousin of lona-js's
 * `SheetRowTree.{visit, markSubsumed, buildFlattened}`. Walks a
 * sheet's row tree depth-first, skipping rows whose parent's active
 * layout subsumes them, and reports the resulting flat list plus
 * the subsumed row IDs.
 *
 * Subsumption is **visual only**. Subsumed rows are excluded from the
 * flat rendered list but still live in the tree — `row.children`,
 * `sheet.stores.tree.children(id)`, etc. keep returning them.
 *
 * Shape overview:
 *   flatten(topLevelRows, deps)         pure, stateless walk
 *   TreeFlattener                        stateful diff tracker for
 *                                        `rendersChildren` — mirrors
 *                                        lona-js's `previousRenders-
 *                                        ChildrenState` / rebuild
 *                                        check.
 *
 * The port reads `RowTypeSpec.{subsumedBy, activeLayout,
 * rendersChildren}` out of `ROW_TYPE_SPECS`. Phase 3d populates the
 * registry from lona-js's per-variant tables; until that lands,
 * unregistered types pass through as non-subsumed, always-renders-
 * children rows (trivial subset of the lona-js behaviour).
 *
 */

import type { Option } from "../core/option.ts";
import type { RowTypeSpec } from "../rows/row-type-spec.ts";
import { SheetRegistries } from "../sheet/sheet-registries.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { SurfaceProjection } from "./projection.ts";
import { SurfaceAnchor } from "./surface-anchor.ts";
import { AliasRow } from "../rows/variants/alias-row.ts";
import { InertRow } from "../rows/variants/inert-row.ts";
import { LayoutRow } from "../rows/variants/layout-row.ts";
import { RendererRow } from "../rows/variants/renderer-row.ts";
import { SourceRow } from "../rows/variants/source-row.ts";

/**
 * What flattening a sheet's row tree needs: the sheet's time axis and, optionally, how to list a
 * row's children.
 */
export interface TreeFlattenDeps {
  /**
   * Sheet's current time axis — passed into
   * `RowTypeSpec.activeLayout(timescale)` so dynamic-layout rows
   * (e.g. calendar switches `activeLayout` between "calendar" in
   * day/week and "calendarMonth" in month) re-derive per timescale.
   */
  readonly timescale: string;

  /**
   * Resolve the wire-type string for a row — the key for
   * `ROW_TYPE_SPECS.get(...)`. The base `ModelRow` does not carry a
   * `wireType` field; callers therefore plumb a resolver so the port
   * stays decoupled from whichever mechanism holds the mapping.
   *
   * Returns `undefined` when the row's wire type isn't known — the
   * port then treats the row as having no spec: no `subsumedBy`, no
   * `activeLayout`, `rendersChildren == true` by default.
   */
  readonly wireTypeOf: (row: ModelRow) => Option<string>;

  /**
   * The lookup tables the rows' wire types resolve through (their sheet's
   * `Sheet.registries`). Absent — a caller with no sheet at hand — the
   * built-ins (`SheetRegistries.BUILTINS`).
   */
  readonly registries?: SheetRegistries;

  /**
   * Alias treatment for the projection.
   *
   * - `"transparent"` (default): legacy rendered-inspection mode;
   *   aliases do not emit and their children are visited.
   * - `"leaf"`: body/legend mode; aliases emit as the visible row
   *   and their hydrated implementation descendants are marked
   *   subsumed.
   */
  readonly aliasProjection?: "transparent" | "leaf";

  /**
   * Optional override for child-iteration. When provided, flatten
   * uses this in place of `row.children` for every row it visits.
   * The body view tree (`BodyViewTree.deriveSheet`) supplies one of
   * these to hoist projection children onto the dedicated
   * header / footer rows without mutating the canonical tree.
   *
   * When unset, defaults to `(row) => row.children` — preserves
   * legacy behaviour for callers that don't use the view tree.
   */
  readonly childrenOf?: (row: ModelRow) => readonly ModelRow[];
}

/**
 * A sheet's row tree flattened depth-first for rendering: the visible rows in order and the ids of
 * the rows their parents' layouts subsume.
 */
export interface FlattenResult {
  /** Flat, depth-first visit order. Subsumed rows are excluded. */
  readonly rows: readonly ModelRow[];
  /**
   * Row IDs that would have appeared in `rows` but were subsumed by
   * a parent's active layout. Tree structure is preserved — these
   * rows are still reachable via `row.children` / `sheet.peekRow`.
   */
  readonly subsumed: ReadonlySet<RowLocalId>;
  /**
   * `rendersChildren` state captured during the walk, one entry per
   * row that appears in `rows`. `TreeFlattener` uses this to detect
   * structure changes between flatten runs.
   */
  readonly rendersChildren: ReadonlyMap<RowLocalId, boolean>;
}

/**
 * What a row sits in (row-subsumption.md, "One container model"): how
 * the container draws its renderer children and its active layout.
 */
export interface LayoutContainer {
  /**
   * `"rows"`: the container lays its children out as rows of their own,
   * so a renderer child is a row. `"cell"`: it draws its renderer
   * children inside its own cell, so a renderer child is subsumed.
   */
  readonly draws: "rows" | "cell";
  /** The container's active layout: rows it does not host are subsumed (`isSubsumedUnderLayout`). */
  readonly layout: Option<string>;
}

export namespace LayoutContainer {
  /**
   * The sheet's header, body and footer: implicit layout containers that
   * draw rows. One value — the top level and a surface row's children
   * sit in the same kind of container.
   */
  export const SECTION: LayoutContainer = Object.freeze({ draws: "rows", layout: null });

  /**
   * The container `row` is for its children, given the layout it passes
   * them: inert chrome draws rows like a section; every other row draws
   * its renderers in its own cell.
   */
  export function of(row: ModelRow, layout: Option<string>): LayoutContainer {
    return { draws: row instanceof InertRow ? "rows" : "cell", layout };
  }
}

/**
 * Pure, stateless flatten + subsumption walk.
 *
 * Given the sheet's top-level rows and the current timescale, returns
 * the post-subsumption flat list in DFS order. `RowTypeSpec` lookups
 * come from the shared `ROW_TYPE_SPECS` registry.
 *
 * Mirrors `SheetRowTree.visit` (tento/tento-lona-js/src/sheet-row-tree.ts:453)
 * exactly — a row is skipped iff its parent's active layout appears
 * in the row's `subsumedBy` list; a parent's children are walked iff
 * its `rendersChildren(attributes)` returns `true` (default).
 */
export function flatten(
  topLevelRows: readonly ModelRow[],
  deps: TreeFlattenDeps,
): FlattenResult {
  const rows: ModelRow[] = [];
  const subsumed = new Set<RowLocalId>();
  const rendersChildren = new Map<RowLocalId, boolean>();
  const aliasProjection = deps.aliasProjection ?? "transparent";
  const getChildren = deps.childrenOf ?? ((r: ModelRow) => r.children);

  const markSubtreeSubsumed = (items: readonly ModelRow[]): void => {
    for (const item of items) {
      subsumed.add(item.id);
      markSubtreeSubsumed(item.children);
    }
  };

  const visit = (
    siblings: readonly ModelRow[],
    container: LayoutContainer,
  ): void => {
    const parentLayout = container.layout;
    for (const row of siblings) {
      // A renderer is drawn by its layout container. Under a row that
      // draws its renderers in its own cell (a source, a layout row, an
      // alias, any data row) it is an internal typed view of that row —
      // auto-hydrated or childSpec-seeded, reachable through
      // `source.defaultRenderer(X)` — and never a standalone row: the
      // parent's plugin paints the cell. Under a container that draws rows
      // (the sheet's header, body and footer; inert chrome) it is a row,
      // bound to data or not: bound to nothing, its cells are empty.
      if (row instanceof RendererRow && container.draws === "cell") {
        subsumed.add(row.id);
        markSubtreeSubsumed(getChildren(row));
        continue;
      }

      // Surface-projection containers (D2). When a `childrenOf`
      // override is present (body view tree case), the view tree
      // has already hoisted projection children to their dedicated
      // surface rows — projection containers are never reached
      // here. When `childrenOf` is the default (legacy callers
      // without surface routing), fall back to the inline behaviour:
      // subsume the projection row and visit its authored children
      // at this position so they still render in body somewhere.
      if (row instanceof InertRow && row.descriptor.kind === "projection") {
        subsumed.add(row.id);
        visit(getChildren(row), LayoutContainer.SECTION);
        continue;
      }

      // Surface rows (`InertRow` with `kind === "surface-container"`)
      // are conceptually part of the sheet itself, not visible rows:
      // only their children produce flat output. Skip emission here
      // so the renderer never tries to dispatch a plugin for the
      // surface row. NOT marked subsumed — cell-layout's
      // `if (row.subsumed) continue` would short-circuit before
      // descending into children, leaving them unpositioned. The
      // surface row keeps its layout entry (zero `cellHeight`,
      // children render at the surface row's offset); only flatten
      // suppresses the row from the visible flat list.
      if (isSurfaceRow(row)) {
        visit(getChildren(row), LayoutContainer.SECTION);
        continue;
      }

      const wireType = deps.wireTypeOf(row);
      const spec = wireType != null
        ? (deps.registries ?? SheetRegistries.BUILTINS).rowTypes.get(wireType)
        : null;
      // Aliases are transparent in flatten — they descend into their
      // children but contribute no layout slot of their own. Children
      // inherit `parentLayout`. Any layout the alias author wants
      // sits as a real row inside `rule.children` (its own
      // `RowTypeSpec.activeLayout` takes over from there).
      const childLayout = row instanceof AliasRow
        ? parentLayout
        : spec?.activeLayout?.(deps.timescale);

      // Inert rows are structural chrome with no named slots:
      // children pass through unsubsumed.
      const nextLayout = row instanceof InertRow
        ? undefined
        : childLayout;

      if (
        parentLayout &&
        isSubsumedUnderLayout(row, parentLayout, spec)
      ) {
        // Subsumed rows don't emit, don't descend — they render
        // inside the parent's slot machinery, not as standalone
        // rows. Structural invalidations still propagate (parent
        // walks children via `row.children`, which is untouched).
        subsumed.add(row.id);
        continue;
      }

      if (row instanceof AliasRow) {
        // An alias with no children, under a container that draws rows,
        // is a row like any other in both projections: its own label,
        // empty cells. Under a row that draws a cell it is part of that
        // cell, as a renderer is.
        if (
          aliasProjection === "leaf" ||
          (container.draws === "rows" && getChildren(row).length === 0)
        ) {
          rows.push(row);
          rendersChildren.set(row.id, false);
          markSubtreeSubsumed(getChildren(row));
          continue;
        }
        visit(getChildren(row), LayoutContainer.of(row, nextLayout ?? null));
        continue;
      }

      rows.push(row);

      const attributes =
        row.sheetReads.rowMeta(row.id).attributes ?? null;
      const rc = spec?.rendersChildren ? spec.rendersChildren(attributes) : true;
      rendersChildren.set(row.id, rc);

      if (!rc) continue;

      visit(getChildren(row), LayoutContainer.of(row, nextLayout ?? null));
    }
  };

  visit(topLevelRows, LayoutContainer.SECTION);

  return { rows, subsumed, rendersChildren };
}

/**
 * True for surface-container rows (`InertRow` with
 * `descriptor.kind === "surface-container"`) — the header/footer surfaces
 * `SystemRows.build()` adds — and for authored surface region rows
 * (`kind === "surface"` with `attributes.surface = "header" | "footer"`,
 * per `SurfaceAnchor`). They're part of the sheet, not visible rows:
 * flatten (and any row enumeration that mirrors it) emits only their
 * children, never the surface row itself.
 *
 * Bare / `surface: "body"` surface rows are NOT matched — they keep
 * their inline (visible inert, CSS-class-only) rendering. Likewise
 * the legacy `header` / `sheet-footer` synthetic system rows are
 * regular visible content rows, not surface wrappers.
 */
export function isSurfaceRow(row: ModelRow): boolean {
  if (!(row instanceof InertRow)) return false;
  if (row.descriptor.kind === "surface-container") return true;
  return SurfaceAnchor.of(row)?.source === "authored";
}

/**
 * Whether a row is visually subsumed by its parent's active layout (drawn inside it, not as its own
 * line); the CLI's tree printer uses it.
 */
export function isSubsumedUnderLayout(
  row: ModelRow,
  parentLayout: string,
  _spec: Option<RowTypeSpec>,
): boolean {
  // Inert rows are structural chrome — they never subsume their
  // children (they have no slots) and are never subsumed themselves
  // (they are display blocks, not data containers).
  if (row instanceof InertRow) return false;

  // A chart canvas owns its data: any data source/series nested under it
  // (the raw data-json source, or a data-series) feeds the chart and must
  // not render as its own row. The canvas still reaches them via
  // `row.children()` (subsumption is visual-only).
  if (row instanceof SourceRow && canonicalLayout(parentLayout) === "canvas") {
    return true;
  }

  // Renderer-id prefix rule: a row is subsumed if it has direct
  // renderer children whose ids start with the parent's layout — the
  // subsumption derives from what renderers the row actually carries
  // (replaces explicit `subsumedBy` arrays). This includes a
  // subcolumn-provider ALIAS such as `alias.chart`, whose direct
  // children are `timeline[column].{points,regression-curve,
  // regression-band}` marks — it's a lane inside the parent grid, not
  // its own row. Every other alias nests its renderers under a
  // data-source/list child, so `hasRendererWithPrefix` (direct
  // children only) leaves them unaffected.
  if (hasRendererWithPrefix(row, parentLayout)) {
    return true;
  }
  if (row instanceof LayoutRow) {
    return row.descriptor.layout !== parentLayout;
  }
  return false;
}

function hasRendererWithPrefix(
  row: ModelRow,
  parentLayout: string,
): boolean {
  const canonical = canonicalLayout(parentLayout);
  const prefix = canonical + ".";
  for (const child of row.children) {
    if (child instanceof RendererRow && child.descriptor.rendererId.startsWith(prefix)) {
      return true;
    }
  }
  return false;
}

function canonicalLayout(name: string): string {
  if (name === "timeline") return "timeline[row]";
  return name;
}

/**
 * Combined flatten + structural-diff result. The `structureChanged`
 * flag is `true` when any row's `rendersChildren` value differs
 * from the previous `TreeFlattener.flatten` call — the invalidation
 * signal callers need to rebuild downstream layout caches.
 */
export interface TreeFlattenStep {
  readonly result: FlattenResult;
  readonly structureChanged: boolean;
}

/**
 * Flattens a sheet's row tree for a render and reports whether its
 * structure changed since the previous call. Mirrors lona-js's
 * `SheetRowTree.invalidateLayout` `needsRebuild` check: on every
 * step, compare the walked `rendersChildren` map against the last
 * recorded one and flag as structurally changed if any row's value
 * differs. The baseline updates only after the diff is computed, so
 * a single call both reports the flag and advances state.
 *
 * The first call returns `structureChanged: false` when the sheet
 * has zero rows, or when every row renders children by default
 * (matches lona-js's empty-map baseline — no spurious first-run
 * invalidation for a row that was going to be in the map anyway).
 * Subsequent calls report `true` iff a row's value flipped, a row
 * appeared, or a row vanished.
 */
export class TreeFlattener {
  #previous: ReadonlyMap<RowLocalId, boolean> = new Map();

  readonly #deps: TreeFlattenDeps;
  constructor(deps: TreeFlattenDeps) {
    this.#deps = deps;
  }

  /**
   * Flatten + diff + record. Returns the fresh `FlattenResult`
   * alongside `structureChanged`. The baseline is replaced after
   * the diff is computed, so the next call sees the result of this
   * one as its "previous" state.
   */
  flatten(topLevelRows: readonly ModelRow[]): TreeFlattenStep {
    const result = flatten(topLevelRows, this.#deps);
    const changed = this.#diff(result.rendersChildren);
    this.#previous = result.rendersChildren;
    return { result, structureChanged: changed };
  }

  #diff(next: ReadonlyMap<RowLocalId, boolean>): boolean {
    if (next.size !== this.#previous.size) return true;
    for (const [id, rc] of next) {
      if (this.#previous.get(id) !== rc) return true;
    }
    return false;
  }

  /** Previously recorded `rendersChildren` snapshot (read-only). */
  get snapshot(): ReadonlyMap<RowLocalId, boolean> {
    return this.#previous;
  }
}

/** Per-surface flatten result. */
export interface FlattenSurfacesResult {
  readonly header: FlattenResult;
  readonly body: FlattenResult;
  readonly footer: FlattenResult;
}

/**
 * Flatten the sheet's row tree per surface. Body is the conventional
 * `flatten(topLevelRows)` — projection containers are subsumed
 * but their children render inline (today's flat-list renderer is
 * surface-unaware). Header and footer come from
 * `SurfaceProjection.projectSheet` and run a fresh `flatten` over
 * their hoisted Node trees.
 *
 * **Caveat:** until 3-surface DOM dispatch lands, the same
 * surface-tagged children are reachable through both
 * `surfaces.body` (inline, where the projection sat in tree order)
 * AND `surfaces.header` / `surfaces.footer` (hoisted). Consumers
 * that want a strict body view (post-projection-stripped) can
 * subtract header/footer ids from body, or recompute from
 * `SurfaceProjection.projectSheet`'s body Node tree directly.
 *
 * When the 3-surface DOM renderer arrives it will switch
 * `flattenSurfaces.body` to a strict subsumption mode and the
 * caveat above goes away.
 */
export function flattenSurfaces(
  topLevelRows: readonly ModelRow[],
  deps: TreeFlattenDeps,
): FlattenSurfacesResult {
  const projected = SurfaceProjection.projectSheet(topLevelRows);
  return {
    header: flatten(projected.header.children.map((n) => n.row), deps),
    body: flatten(topLevelRows, deps),
    footer: flatten(projected.footer.children.map((n) => n.row), deps),
  };
}
